import React from 'react';
import { motion } from 'framer-motion';
import { getPointerColor } from '../../utils/pointerUtils';

export const LinkedListRenderer = ({ listStr, memory, isLight, textColor, mutedText, cycleTo }) => {
  if (!listStr) return null;

  // Parse list string: "1->2->3" or "Left <-> (1,1) <-> Right"
  const parts = listStr.split(/<\->|->/).map(s => s.trim());

  // Constants for positioning cyclic arrow
  const nodeWidth = 54;
  const arrowGap = 24;
  const nodeTotalWidth = nodeWidth + arrowGap;
  const nodeHeight = 34;

  return (
    <div style={{ position: 'relative', padding: '20px 0 100px 0', width: 'fit-content' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0', flexWrap: 'nowrap' }}>
        {parts.map((part, i) => {
          const isLast = i === parts.length - 1;
          const isDummy = part.toLowerCase().includes('left') || part.toLowerCase().includes('right') || part.toLowerCase().includes('head') || part.toLowerCase().includes('tail') || part.toLowerCase().includes('dummy');

          // Find if any memory variable points to this node value
          const pointers = Object.entries(memory || {})
            .filter(([_, v]) => String(v).includes(part))
            .map(([key]) => key);

          return (
            <React.Fragment key={i}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                {/* Node Pointers */}
                <div style={{ height: '30px', display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'flex-end', marginBottom: '4px' }}>
                  {pointers.map(ptr => {
                    const pc = getPointerColor(ptr);
                    return (
                      <motion.div
                        key={ptr}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        style={{
                          fontSize: '10px',
                          fontWeight: 900,
                          color: pc.text,
                          background: pc.bg,
                          padding: '1px 6px',
                          borderRadius: '2px', // Sharper corners
                          border: `1px solid ${pc.border}`,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {ptr}
                      </motion.div>
                    );
                  })}
                </div>

                {/* Node Visualization (GfG style: Sharp, distinct boxes) */}
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  style={{
                    display: 'flex',
                    border: `2px solid ${isDummy ? (isLight ? '#94a3b8' : '#475569') : (isLight ? '#0f172a' : '#cbd5e1')}`,
                    borderRadius: '2px', // Sharp
                    background: isLight ? '#fff' : '#1e1e24',
                    overflow: 'hidden',
                    minWidth: `${nodeWidth}px`,
                    height: `${nodeHeight}px`
                  }}
                >
                  {/* Data Compartment */}
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 8px',
                    fontSize: '13px',
                    fontFamily: 'JetBrains Mono, monospace',
                    fontWeight: 700,
                    borderRight: `2px solid ${isLight ? '#0f172a' : '#cbd5e1'}`,
                    color: isDummy ? mutedText : textColor
                  }}>
                    {part}
                  </div>
                  {/* Next Compartment */}
                  <div style={{
                    width: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: isLight ? '#f1f5f9' : '#111114'
                  }}>
                    <div style={{ width: '8px', height: '8px', border: '1px solid #3b82f6', borderRadius: '1px' }} />
                  </div>
                </motion.div>
                
                {/* Node Index label */}
                <span style={{ fontSize: '9px', color: mutedText, marginTop: '4px', fontWeight: 600 }}>{i}</span>
              </div>

              {/* Edge Arrow (Horizontal) */}
              {!isLast && (
                <div style={{ display: 'flex', alignItems: 'center', padding: '0', alignSelf: 'center', marginTop: '34px' }}>
                  <div style={{ width: `${arrowGap}px`, height: '2px', background: isLight ? '#0f172a' : '#cbd5e1', position: 'relative' }}>
                    <div style={{
                      position: 'absolute',
                      right: '-2px',
                      top: '-3px',
                      width: '0',
                      height: '0',
                      borderTop: '4px solid transparent',
                      borderBottom: '4px solid transparent',
                      borderLeft: `6px solid ${isLight ? '#0f172a' : '#cbd5e1'}`
                    }} />
                  </div>
                </div>
              )}

              {isLast && cycleTo === undefined && (
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', alignSelf: 'center', marginTop: '34px', fontSize: '10px', color: mutedText, fontWeight: 900, letterSpacing: '1px' }}>
                  NULL
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Cyclic Arrow Rendering (Structured GfG style) */}
      {cycleTo !== undefined && (
        <svg 
          style={{ 
            position: 'absolute', 
            top: '88px', // Bottom of node box
            left: '0',
            width: '100%',
            height: '100px',
            overflow: 'visible',
            pointerEvents: 'none'
          }}
        >
          {(() => {
            // Start from bottom center of last node's NEXT box
            const startX = (parts.length - 1) * nodeTotalWidth + 42; 
            // End at bottom center of target node
            const endX = cycleTo * nodeTotalWidth + 27; 
            const dropY = 60;
            const path = `M ${startX} 0 L ${startX} ${dropY} L ${endX} ${dropY} L ${endX} 0`;
            const color = isLight ? '#0f172a' : '#cbd5e1';
            
            return (
              <g>
                <path 
                  d={path} 
                  fill="none" 
                  stroke={color} 
                  strokeWidth="2" 
                />
                {/* Arrowhead at target node pointing UP */}
                <path 
                  d={`M ${endX - 4} 8 L ${endX} 0 L ${endX + 4} 8`}
                  fill="none"
                  stroke={color}
                  strokeWidth="2"
                />
              </g>
            );
          })()}
        </svg>
      )}
    </div>
  );
};
